import {MessageOutlined} from '@ant-design/icons';
import {Button, Flex, Tag, theme, Typography} from 'antd';
import CommentsDialog from 'Pages/SRDashboard/components/ResultsTable/CommentsDialog';
import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {NoSearchResults} from 'Components';
import {MEMBER_SEARCH} from 'Pages/SearchMember/searchConstants';
import {searchSRByID} from 'Redux/Slices/selectedSR';
import {SortOrder} from 'Utils/enums';
import {FORMAT_TYPE} from 'Utils/constants/DateFormat';
import {dateConversionIsoToLocal} from 'Utils/helpers/DateValidation';
import {sortTable} from 'Utils/helpers/SRListHelpers';

const {Text} = Typography;

// SR status strings are free-form (see Utils/constants/genericConstants
// statusList) — this is a pragmatic keyword heuristic for the list-card
// badge color, not an exhaustive/authoritative status taxonomy.
const statusTagColor = (status: string): string => {
  const s = (status || '').toLowerCase();
  if (
    ['cancelled', 'denied', 'rejected', 'declined', 'failed', 'escalat'].some((k) => s.includes(k))
  ) {
    return 'error';
  }
  if (
    [
      'pending',
      'assessing',
      'requested',
      'quoted',
      'missing',
      'fee not paid',
      'review',
      'hold',
    ].some((k) => s.includes(k))
  ) {
    return 'warning';
  }
  if (
    ['open', 'approved', 'confirmed', 'completed', 'supplied', 'passed'].some((k) => s.includes(k))
  ) {
    return 'success';
  }
  return 'default';
};

// `.list-card` pattern from the AST Console Revamp mockup's `#tab-sr`: a
// stacked list of bordered rows (title + meta on the left, status badge +
// action on the right) — replaces the previous data-table rendering.
const SRListView = (props) => {
  const dispatch = useDispatch();
  const {SRListData} = props;
  const {token} = theme.useToken();
  // No per-column sort UI in the list-card layout (the mockup's `.list-card`
  // rows don't have one) — only the setters are needed, to feed `sortTable`'s
  // initial default sort (by createdOn) below.
  const [, setCriteriaSort] = useState('createdOn');
  const [, setSortOrder] = useState(SortOrder.ASCENDING);
  const [tableData, setTableData] = useState<any[]>([]);
  const tenantConfig = useSelector((state: any) => state?.login?.tenantConfig);
  const [noResults, setNoResults] = useState(false);
  const [openCommentsDialog, setOpenCommentsDialog] = useState(false);
  const [commentData, setCommentData] = useState('');

  useEffect(() => {
    if (SRListData && SRListData?.length > 0) {
      setNoResults(false);
      parseUnfilteredData(SRListData);
    } else {
      setNoResults(true);
    }
  }, [SRListData]);

  const parseUnfilteredData = (data) => {
    const temp: any = [];
    (data || []).forEach((item: any) => {
      temp.push({
        type: item?.type,
        id: item?.id,
        status: item?.status,
        serviceType: item?.additionalData?.service,
        summary: item?.summary,
        createdOn: item?.audit?.createdOn
          ? dateConversionIsoToLocal(
              item.audit?.createdOn,
              FORMAT_TYPE.DATE_TIME,
              '',
              tenantConfig.locale,
            )
          : '',
        modifiedOn: item?.audit?.modifiedOn
          ? dateConversionIsoToLocal(
              item.audit?.modifiedOn,
              FORMAT_TYPE.DATE_TIME,
              '',
              tenantConfig.locale,
            )
          : '',
        srData: item,
      });
    });
    let resp = sortTable(temp, 'createdOn', 'createdOn', SortOrder.ASCENDING, tenantConfig.locale);
    setCriteriaSort(resp.currentSortCriteria);
    setTableData(resp.tableData);
    setSortOrder(resp.newSortOrder);
  };

  const onRowClick = async (rowData) => {
    let {srData} = rowData;
    let srPayload = {
      id: srData.id,
    };
    let response = await dispatch(searchSRByID(srPayload));
    if (response) {
      props.handleSRRowClick();
    }
  };

  const showComments = (e: React.MouseEvent, row: any) => {
    e.stopPropagation();
    setCommentData(row?.srData?.additionalData?.comments);
    setOpenCommentsDialog(true);
  };

  return (
    <div>
      <CommentsDialog
        openCommentsDialog={openCommentsDialog}
        setOpenCommentsDialog={setOpenCommentsDialog}
        comments={commentData}
      />
      {noResults ? (
        <NoSearchResults CSTexts={MEMBER_SEARCH.no_search_result} />
      ) : (
        tableData.map((row) => (
          <Flex
            key={row.id}
            align="center"
            justify="space-between"
            gap={16}
            onClick={() => onRowClick(row)}
            style={{
              background: token.colorBgContainer,
              border: `1px solid ${token.colorBorderSecondary}`,
              borderRadius: token.borderRadiusLG,
              padding: '14px 16px',
              marginBottom: 10,
              cursor: 'pointer',
            }}>
            <Flex vertical gap={4} style={{minWidth: 0}}>
              <Text strong style={{fontSize: 13}}>
                {row.id} · {row.type}
              </Text>
              <Text style={{fontSize: 12, color: token.colorTextSecondary}} ellipsis>
                Created {row.createdOn}
                {row.serviceType ? ` · ${row.serviceType}` : ''}
                {row.summary ? ` · ${row.summary}` : ''}
              </Text>
            </Flex>
            <Flex align="center" gap={14} style={{flexShrink: 0}}>
              <Tag color={statusTagColor(row.status)} style={{margin: 0}}>
                {row.status}
              </Tag>
              <Button
                type="text"
                size="small"
                icon={<MessageOutlined />}
                onClick={(e) => showComments(e, row)}
              />
            </Flex>
          </Flex>
        ))
      )}
    </div>
  );
};

export default SRListView;
